import { NavContext } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { Capacitor } from '@capacitor/core';
import { User } from '@supabase/supabase-js';

import { useAccount, useNetwork, useTracker, useVersion } from '@/contexts';
import {
  User as UserRecord,
  createUser,
  getUser,
  removeNotificationToken
} from '@/data/Users';
import { useToast } from '@/hooks';
import { unregisterNotifications } from '@/utils/notifications';
import { ROUTES } from '@/utils/routes';
import { supabase } from '@/utils/supabase';
import { EVENTS } from '@/utils/tracker';
import {
  UseQueryResult,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

interface AuthContextType {
  authenticated: boolean;
  user: User | null;
  signIn: (showLoading?: boolean) => Promise<UserRecord | null>;
  signOut: (showLoading?: boolean, clearSavedAccounts?: boolean) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { navigate } = useContext(NavContext);
  const { connected } = useNetwork();
  const { captureEvent, identifyUser, resetTracker } = useTracker();
  const { forceUpdate, maintenanceModeEnabled } = useVersion();
  const { clearAllAccounts } = useAccount();
  const queryClient = useQueryClient();
  const displayToast = useToast();

  const [showLoading, setShowLoading] = useState(false);

  const isNativePlatform = Capacitor.isNativePlatform();

  const {
    data: authData,
    isLoading: loading
  }: UseQueryResult<
    {
      user: User | null;
      authenticated: boolean;
    },
    Error
  > = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      if (!connected) {
        const existing = queryClient.getQueryData<{
          user: User | null;
          authenticated: boolean;
        }>(['auth', 'user']);
        if (existing?.authenticated) {
          return existing;
        }

        return {
          user: null,
          authenticated: false
        };
      }

      try {
        const {
          data: { user },
          error
        } = await supabase.auth.getUser();

        if (error) {
          throw new Error(error.message);
        }

        if (user) {
          identifyUser({ distinctId: user.id });
          captureEvent({ event: EVENTS.USER_SIGNED_IN });
          return { user, authenticated: true };
        } else {
          throw new Error('User not found.');
        }
      } catch {
        const existing = queryClient.getQueryData<{
          user: User | null;
          authenticated: boolean;
        }>(['auth', 'user']);
        if (existing?.authenticated) {
          return existing;
        }

        resetTracker();
        return { user: null, authenticated: false };
      }
    },
    enabled: connected,
    refetchOnWindowFocus: false
  });

  const { authenticated, user } = authData ?? {
    authenticated: false,
    user: null
  };

  const signIn = useCallback(
    async (withLoading: boolean = true) => {
      if (withLoading) setShowLoading(true);
      try {
        const {
          data: { user },
          error
        } = await supabase.auth.getUser();

        if (error) {
          throw new Error(error.message);
        }

        if (!user) {
          throw new Error('User not found.');
        }

        const existingUser = await queryClient.fetchQuery({
          queryKey: ['user', user.id],
          queryFn: () => getUser(user.id)
        });
        if (!existingUser) {
          const newUser = await queryClient.fetchQuery({
            queryKey: ['createUser', user.id],
            queryFn: () =>
              createUser({
                id: user.id,
                notification_tokens: null
              })
          });
          if (newUser) {
            queryClient.setQueryData(['auth', 'user'], {
              user,
              authenticated: true
            });
            identifyUser({ distinctId: user.id });
            captureEvent({ event: EVENTS.USER_SIGNED_IN });

            return newUser;
          } else {
            throw new Error('User not created.');
          }
        }
        queryClient.setQueryData(['auth', 'user'], {
          user,
          authenticated: true
        });
        identifyUser({ distinctId: user.id });
        captureEvent({ event: EVENTS.USER_SIGNED_IN });

        return existingUser;
      } catch (error) {
        displayToast({
          message: 'Error logging in. Please contact support.',
          position: 'bottom'
        });
        return null;
      } finally {
        setShowLoading(false);
      }
    },
    [identifyUser, captureEvent, queryClient, displayToast]
  );

  const signOut = useCallback(
    async (
      withLoading: boolean = true,
      clearSavedAccounts: boolean = false
    ) => {
      if (withLoading) setShowLoading(true);
      try {
        if (user?.id && isNativePlatform) {
          const notificationToken = await unregisterNotifications();

          if (notificationToken) {
            await removeNotificationToken({
              userId: user.id,
              token: notificationToken
            });
          }
        }

        const { error } = await supabase.auth.signOut();

        if (error) {
          throw new Error(error.message);
        }

        queryClient.setQueryData(['auth', 'user'], {
          user: null,
          authenticated: false
        });
        resetTracker();

        if (clearSavedAccounts) {
          await clearAllAccounts();
        }

        navigate(ROUTES.WELCOME);
      } catch {
        displayToast({
          message: 'Error logging out. Please contact support.',
          position: 'bottom'
        });
      } finally {
        setShowLoading(false);
      }
    },
    [
      resetTracker,
      navigate,
      queryClient,
      user?.id,
      isNativePlatform,
      clearAllAccounts
    ]
  );

  useEffect(() => {
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        queryClient.setQueryData(['auth', 'user'], {
          user: null,
          authenticated: false
        });
        resetTracker();
        return;
      }

      if (session?.user) {
        queryClient.setQueryData(['auth', 'user'], {
          user: session.user,
          authenticated: true
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient, resetTracker]);

  useEffect(() => {
    if (!connected || forceUpdate || maintenanceModeEnabled) {
      queryClient.cancelQueries({ queryKey: ['auth', 'user'] });
      setShowLoading(false);
    }
  }, [forceUpdate, maintenanceModeEnabled, connected, queryClient]);

  return (
    <AuthContext.Provider
      value={{
        authenticated: authenticated ?? false,
        user: user ?? null,
        signIn,
        signOut,
        loading: showLoading || loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
